import React, { useState, useCallback, useEffect } from "react";
import { IoMdAdd } from "react-icons/io";
import { IoHeartOutline, IoHeartSharp, IoCloseSharp } from "react-icons/io5";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useSession } from "../../ApiCalls/useSession";
import { BASE_URL, USER_URL } from "../../constant/api";
import AddToCartModel from "../Home/AddToCartModel/AddToCartModel";

function MyOrderProductCard({ products = [], title, bg, layout = "vertical-3-scroll" }) {
  useSession();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const layoutClass = {
    "vertical-3-scroll": "flex flex-col space-y-2 max-h-[870px] overflow-y-auto", // Vertical scrolling
    "vertical-3-horizontal-2": "grid grid-cols-2 gap-2 max-h-[930px] overflow-y-auto", // Vertical first 3 and then horizontal 2
    "vertical-2-horizontal-2": "grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[700px] overflow-y-auto", // First 2 vertically, then horizontal 2
  };

  // Open Modal
  const openModal = useCallback((product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  }, []);

  // Close Modal
  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedProduct(null);
    setQuantity(1);
  }, []);

  useEffect(() => {
    const getWishlist = async () => {
      // const token = localStorage.getItem("token");
      // const sessionId = localStorage.getItem("sessionid");
      // const headers = token ? { Authorization: `Token ${token}` } : { "Session-Id": sessionId };

      try {
        const response = await USER_URL.get(`/products/wishlist/`);
        setWishlist(response.data.items.map((item) => item.product_id));
      } catch (error) {
        console.error("Error fetching wishlist:", error);
      }
    };

    getWishlist();
  }, []);

  // Toggle Wishlist
  const toggleWishlist = async (product_id) => {
    try {
      const isWishlisted = wishlist.includes(product_id);
      // const token = localStorage.getItem("token");
      // const sessionId = localStorage.getItem("sessionid");
      // const headers = token ? { Authorization: `Token ${token}` } : { "Session-Id": sessionId };

      await USER_URL.post(`/products/wishlist/`, { product_id });

      setWishlist((prev) =>
        isWishlisted
          ? prev.filter((id) => id !== product_id)
          : [...prev, product_id]
      );
    } catch (error) {
      console.error("Error toggling wishlist:", error);
    }
  };

  const handleDeleteWishlistItem = async (productId) => {
    // const token = localStorage.getItem("token");
    // let sessionId = localStorage.getItem("sessionid");

    // const headers = token
    //   ? { Authorization: `Token ${token}` }
    //   : { "Session-Id": sessionId };

    try {
      const response = await USER_URL.delete(`/products/wishlist/${productId}/`);

      if (response.data.Status === "1") {
        setWishlist((prev) => prev.filter((id) => id !== productId));
        console.log("Item deleted successfully:", response.data);
      } else {
        console.error("Unexpected response:", response.data);
      }
    } catch (error) {
      console.error(`Error deleting item ${productId}:`, error);
    }
  };

  // Add to Cart
  const addToCart = async (product, quantity) => {
    if (!product || !product.id) {
      console.error("Invalid product or missing product ID");
      return;
    }
    setLoading(true);
    try {
      // const token = localStorage.getItem("token");
      // const sessionId = localStorage.getItem("sessionid");
      // const headers = token ? { Authorization: `Token ${token}` } : { "Session-Id": sessionId };

      if (quantity === 1) {
        quantity = product.min_order_quantity;
      }

      await USER_URL.post(
        `/orders/cart/`,
        { product_id: product.id, quantity: quantity },
        // { headers }
      );

      navigate("/cart");
      closeModal();
    } catch (error) {
      console.error("Error adding product to cart:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ backgroundColor: bg }} className="">
      <h1 className="text-md font-bold mb-3 mt-3">{title}</h1>
      <div className={`${layoutClass[layout]} ${layout === "vertical-3-scroll" ? " overflow-y-auto" : ""}`}>
        {products.length > 0 ? (
          products.map((product, index) => (
            <div
              key={product.id}
              className={`rounded-lg bg-white p-3 border-2 border-gray-300 shadow-md ${
                layout === "vertical-3-horizontal-2" && index >= 3
                  ? "md:col-span-1" // Adjust horizontal layout after the first 3 cards
                  : ""
              } ${
                layout === "vertical-2-horizontal-2" && index >= 2
                  ? "md:col-span-1" // Start displaying in 2 columns after the first 2
                  : ""
              }`}
            >
              <div className="flex justify-between items-center text-sm text-brand-light-gray">
                {product.offer_percent && (
                  <span className="rounded-lg bg-red-600 p-1 px-2">
                    {product.offer_percent ? `-${product.offer_percent}%` : ""}
                  </span>
                )}
                <span
                  className="text-red-700 text-2xl cursor-pointer"
                  onClick={() =>
                    wishlist.includes(product.id)
                      ? handleDeleteWishlistItem(product.id)
                      : toggleWishlist(product.id)
                  }
                >
                  {wishlist.includes(product.id) ? (
                    <IoHeartSharp />
                  ) : (
                    <IoHeartOutline />
                  )}
                </span>
              </div>
              <Link to={`/product-details/${product.id}`}>
                <img
                  src={
                    product.product_images?.[0]?.image
                      ? product.product_images[0].image.startsWith(BASE_URL)
                        ? product.product_images[0].image
                        : `${BASE_URL}${product.product_images[0].image}`
                      : "/images/default-product.png"
                  }
                  alt={product.name || "Product"}
                  className="mb-2 h-32 w-full object-contain my-2 transition transform hover:scale-105"
                  onError={(e) => {
                    e.target.src = "/images/default-product.png";
                  }}
                />
              </Link>

              <div className="flex flex-col items-start">
                <h3 className="font-semibold hover:text-green-500">
                  <Link to={`/product-details/${product.id}`}>
                    {product.name || "No Name Available"}
                  </Link>
                </h3>
                <p className="line-clamp-2 text-sm text-gray-600">
                  {product.description || "No description available"}
                </p>
                <div className="flex justify-between w-full">
                  <p className="text-lg font-bold">
                    {product.price ? ` ${product.currency_symbol}${product.price}` : "N/A"}
                  </p>
                  <span
                    className="rounded-lg bg-brand-primary p-2 text-white cursor-pointer"
                    onClick={() => openModal(product)}
                  >
                    <IoMdAdd />
                  </span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <p className="text-center text-gray-500">No products available.</p>
        )}
      </div>

      {/* Product Modal */}
      <AddToCartModel
        selectedProduct={selectedProduct}
        isModalOpen={isModalOpen}
        closeModal={closeModal}
      />
    </div>
  );
}

export default MyOrderProductCard;
